import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Icon, ICONS } from '@/lib/icons'
import { cn } from '@/lib/utils'
import type { KnockoutMatch, Player } from '@/types/api'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface BracketMatchCardProps {
  match: KnockoutMatch
  playersById: Record<string, Player>
  resolveSourceLabel: (sourceKey?: string, isBronzeMatch?: boolean) => string | undefined
  onReportMatch?: (match: KnockoutMatch) => void
  onEditMatch?: (match: KnockoutMatch) => void
}

function getWinnerId(match: KnockoutMatch): string | undefined {
  if (!match.completed) return undefined
  switch (match.outcome) {
    case 'KNOCKOUT_MATCH_OUTCOME_PLAYER_A_WIN':
      return match.playerAId
    case 'KNOCKOUT_MATCH_OUTCOME_PLAYER_B_WIN':
      return match.playerBId
    default:
      return match.winnerId
  }
}

export function BracketMatchCard({ match, playersById, resolveSourceLabel, onReportMatch, onEditMatch }: BracketMatchCardProps) {
  const { t } = useTranslation()
  const winnerId = useMemo(() => getWinnerId(match), [match])
  const isLocked = Boolean(match.isLocked)
  const isBronzeMatch = match.roundNumber === 0

  const playerAName = match.playerAId
    ? playersById[match.playerAId]?.displayName || t('players.unknownPlayer', 'Unknown player')
    : resolveSourceLabel(match.sourceMatchA, isBronzeMatch) || t('knockout.match.tbd', 'TBD')

  const playerBName = match.playerBId
    ? playersById[match.playerBId]?.displayName || t('players.unknownPlayer', 'Unknown player')
    : resolveSourceLabel(match.sourceMatchB, isBronzeMatch) || t('knockout.match.tbd', 'TBD')

  const canReport = Boolean(onReportMatch && match.playerAId && match.playerBId && !match.completed && !isLocked)
  const showReportButton = Boolean(onReportMatch && match.playerAId && match.playerBId && !match.completed)

  const scoreLabel = match.completed
    ? `${match.scoreA ?? '-'} – ${match.scoreB ?? '-'}`
    : t('knockout.match.awaitingResult', 'Awaiting result')

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-4 space-y-3">
        <div className="space-y-2">
          <PlayerRow
            name={playerAName}
            isWinner={winnerId !== undefined && winnerId === match.playerAId}
            isAssigned={Boolean(match.playerAId)}
            score={match.scoreA}
          />
          <PlayerRow
            name={playerBName}
            isWinner={winnerId !== undefined && winnerId === match.playerBId}
            isAssigned={Boolean(match.playerBId)}
            score={match.scoreB}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{scoreLabel}</span>
          {match.matchTime && (
            <time dateTime={match.matchTime}>
              {new Date(match.matchTime).toLocaleString()}
            </time>
          )}
        </div>

        {match.completed && winnerId && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Icon icon={ICONS.trophy} width={14} height={14} />
            {t('knockout.match.winner', 'Winner')}: {playersById[winnerId]?.displayName || t('players.unknownPlayer', 'Unknown player')}
          </Badge>
        )}

        {isLocked && match.lockedReason && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon icon={ICONS.lock} width={14} height={14} />
            <span>{t(`tournament.locked.reasons.${match.lockedReason}`, match.lockedReason)}</span>
          </div>
        )}

        {showReportButton && (
          <Button
            type="button"
            size="sm"
            className="w-full"
            disabled={!canReport}
            onClick={() => {
              if (!canReport) return
              onReportMatch?.(match)
            }}
          >
            <Icon icon={ICONS.edit} width={16} height={16} />
            <span className="ml-2">{t('knockout.match.report', 'Report result')}</span>
          </Button>
        )}

        {match.completed && !isLocked && onEditMatch && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => onEditMatch(match)}
          >
            <Icon icon={ICONS.edit} width={16} height={16} />
            <span className="ml-2">{t('common.edit')}</span>
          </Button>
        )}
      </div>
    </div>
  )
}

interface PlayerRowProps {
  name: string
  isWinner: boolean
  isAssigned: boolean
  score?: number
}

function PlayerRow({ name, isWinner, isAssigned, score }: PlayerRowProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-md border px-3 py-2 text-sm',
        isWinner ? 'border-success bg-success-muted text-success-foreground dark:border-success/70 dark:bg-success/10' : 'border-border',
        !isAssigned && 'italic text-muted-foreground'
      )}
    >
      <span className="truncate font-medium">{name}</span>
      {typeof score === 'number' && (
        <span className="ml-2 min-w-[1.5rem] text-right font-semibold">{score}</span>
      )}
    </div>
  )
}
